import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect
} from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Typography } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import ActionDelete from '@material-ui/icons/Delete';
import inflection from 'inflection';
import {
  Button,
  TopToolbar,
  useTranslate,
  useNotify,
  List,
  useRefresh,
  useQueryWithStore,
  useUnselectAll,
  RecordContextProvider,
  useDataProvider
} from 'react-admin';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';

import Confirm from '../../../core/components/forked/layout/Confirm';
import UserNotificationApi from '../../../core/apis/UserNotificationApi';
import UserNotificationCreate from './UserNotificationCreate';
import UserNotificationDatagrid from './UserNotificationDatagrid';
import useUserNotificationsDataProvider from './UserNotificationDataProvider';
import { UserNotificationContext } from './UserNotificationContext';
import EditToolbar from '../../../core/components/edit/EditToolbar';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import CustomSimpleForm from '../../../core/components/form/CustomSimpleForm';

const useStyles = makeStyles({
  listContainer: {
    margin: 0,
    marginTop: -25
  }
});

const useCustomFormStyles = makeStyles({
  customForm: {
    '& > *:first-child': {
      padding: 0
    }
  },
  fullWidth: {
    width: '100%'
  }
});

const CustomForm = props => {
  const {
    data = [],
    customRef,
    hasBulkActions,
    currentsort,
    defaultTitle,
    displayedFilters,
    filtervalues,
    hasCreate,
    hideFilter,
    onToggleItem,
    onUnselectItems,
    perPage,
    selectedIds,
    setFilters,
    setPage,
    setPerPage,
    setSort,
    showFilter,
    totalPages,
    filterValues,
    setContext,
    context,
    hasWriteUserNotificationPermission,
    refetch,
    ...rest
  } = props;
  const classes = useCustomFormStyles();

  const currentData = pick(data, props.ids);

  return (
    <CustomSimpleForm
      {...omit(rest, ['loaded', 'loading', 'currentSort'])}
      initialValues={currentData}
      className={classes.customForm}
      toolbar={
        <EditToolbar
          {...rest}
          hasEditPermission={hasWriteUserNotificationPermission}
          deletable={false}
        />
      }
    >
      <>
        {/** to prevent div from receiving injected props from parent: basePath */}
        <div className={classes.fullWidth}>
          {React.cloneElement(props.children, {
            ...omit(props, ['children', 'setContext', 'customRef', 'save']),
            notifications: Object.values(currentData)
          })}
        </div>
      </>
    </CustomSimpleForm>
  );
};

const useBulkActionsToolbarButtonStyles = makeStyles(theme => ({
  deleteButton: {
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: alpha(theme.palette.error.main, 0.12),
      // Reset on mouse devices
      '@media (hover: none)': {
        backgroundColor: 'transparent'
      }
    }
  }
}));

const BulkActionsToolbarButtons = props => {
  const { resource, userId, selectedIds, context } = props;
  const classes = useBulkActionsToolbarButtonStyles();
  const translate = useTranslate();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll();
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { data, ids } = context;
  const notifyServerError = useNotifyServerError();

  const currentData = pick(data, ids);

  const handleDelete = useCallback(async () => {
    try {
      setIsDeleting(true);
      const dataToKeep = Object.values(currentData)
        .filter(item => !selectedIds.includes(item.id))
        .map(item => omit(item, 'id'));
      await UserNotificationApi.update(dataToKeep, userId);

      setIsDeleting(false);
      setOpenConfirmDelete(false);
      unselectAll('usernotifications');
      refresh();
    } catch (error) {
      setIsDeleting(false);
      setOpenConfirmDelete(false);
      notifyServerError(error);
    }
  }, [
    selectedIds,
    userId,
    unselectAll,
    refresh,
    currentData,
    notifyServerError
  ]);

  return (
    <>
      <Button
        className={classes.deleteButton}
        onClick={() => setOpenConfirmDelete(true)}
        label="ra.action.delete"
      >
        <ActionDelete />
      </Button>
      <Confirm
        isOpen={openConfirmDelete}
        loading={isDeleting}
        title="ra.message.bulk_delete_title"
        content="ra.message.bulk_delete_content"
        translateOptions={{
          smart_count: selectedIds.length,
          name: inflection.humanize(
            translate(`resources.${resource}.name`, {
              smart_count: selectedIds.length,
              _: inflection.inflect(resource, selectedIds.length)
            }),
            true
          )
        }}
        onConfirm={handleDelete}
        onClose={() => setOpenConfirmDelete(false)}
      />
    </>
  );
};

const useActionsToolbarStyles = makeStyles({
  addButton: {
    border: '1px solid #E0E0E0',
    background: '#FFFFFF',
    alignSelf: 'center'
  }
});

const ActionsToolbar = props => {
  const {
    dialogProps,
    data = {},
    ids,
    hasWriteUserNotificationPermission,
    disabled
  } = props;
  const classes = useActionsToolbarStyles();
  const [
    openNotificationCreateDialog,
    setOpenNotificationCreateDialog
  ] = useState(false);

  const handleNotificationCreateDialogToggle = useCallback(open => {
    setOpenNotificationCreateDialog(open);
  }, []);

  const currentData = pick(data, ids);

  return (
    <Grid container justifyContent="space-between" alignItems="center">
      <Typography variant="h5">Notifications</Typography>
      <TopToolbar>
        {hasWriteUserNotificationPermission && (
          <Button
            onClick={() => {
              setOpenNotificationCreateDialog(true);
            }}
            label="Add Notification"
            variant="outlined"
            color="primary"
            disableFocusRipple
            className={classes.addButton}
            disabled={disabled}
            id="add-notification"
          />
        )}
      </TopToolbar>
      <UserNotificationCreate
        {...dialogProps}
        notifications={Object.values(currentData)}
        showDialog={openNotificationCreateDialog}
        handleDialogToggle={handleNotificationCreateDialogToggle}
        operation="Create"
        resource="usernotifications"
        basePath="/usernotifications"
        okButtonLabel="Add"
        title=" "
        popupTitle="Add Notification"
      />
    </Grid>
  );
};

const useEmptyStyles = makeStyles({
  emptyText: {
    color: '#424242',
    fontSize: '14px',
    fontWeight: '400',
    display: 'inline-block',
    fontFamily: '"Segoe UI",Arial,sans-serif'
  }
});

const Empty = props => {
  const classes = useEmptyStyles();
  return (
    <div>
      <ActionsToolbar {...props} />
      <div className={classes.emptyText}>
        There are no Notifications to be displayed
      </div>
    </div>
  );
};

const CustomProvider = customProps => {
  const {
    data,
    ids,
    context,
    setContext,
    providerNotificationContext
  } = customProps;
  useEffect(() => {
    if (!isEqual(context.data, data)) {
      setContext({ ...context, data });
    }
    if (!isEqual(context.ids, ids)) {
      setContext({ ...context, ids });
    }
  }, [data, ids, context, setContext]);

  return (
    <UserNotificationContext.Provider value={providerNotificationContext}>
      {React.cloneElement(customProps.children, {
        ...omit(customProps, ['children', 'providerNotificationContext'])
      })}
    </UserNotificationContext.Provider>
  );
};

const UserNotificationTab = props => {
  const {
    documentTypes,
    userphonetypes,
    useraddresstypes,
    id: userId,
    permissions,
    notificationTabKey
  } = props;

  const classes = useStyles();
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const refresh = useRefresh();
  const formProps = useRef(null);
  const dataProvider = useDataProvider();

  // need to refresh the useraddress list because if we get it through useSelector it's not fresh
  const { data: allAddresses = [] } = useQueryWithStore({
    type: 'getList',
    resource: 'useraddresses',
    payload: {
      filter: {
        userId: userId
      }
    }
  });

  const [notificationContext, setNotificationContext] = useState({
    saving: false
  });

  const userAddressTypesAll = useMemo(() => Object.values(useraddresstypes), [
    useraddresstypes
  ]);

  const userAddressTypesAvailable = useMemo(() => {
    const addressesTypesIds = {};
    allAddresses.forEach(
      address => (addressesTypesIds[address.addressTypeId] = true)
    );
    return userAddressTypesAll.filter(types => addressesTypesIds[types.id]);
  }, [allAddresses, userAddressTypesAll]);

  const docTypeChoices = useMemo(() => Object.values(documentTypes), [
    documentTypes
  ]);

  const providerNotificationContext = useMemo(
    () => ({ notificationContext, setNotificationContext }),
    [notificationContext, setNotificationContext]
  );

  const { fundInvestors, funds, investors } = useUserNotificationsDataProvider({
    id: userId,
    notifications: notificationContext.data,
    key: notificationTabKey
  });

  const [fundChoices, setFundChoices] = useState([]);
  const [investorChoices, setInvestorChoices] = useState([]);
  const [fundInvestorsMap, setFundInvestorsMap] = useState({});
  const [authorizationsData, setAuthorizationsData] = useState({});

  useEffect(() => {
    dataProvider
      .getList('userauthorizations', {
        filter: { userId: props.id }
      })
      .then(({ data }) => {
        setAuthorizationsData(data);
      });
  }, [dataProvider, props.id, notificationTabKey]);

  useEffect(() => {
    if (fundInvestors.data.length) {
      const _funds = {};
      const _investors = {};
      const _fundInvestorsMap = {};

      let filterAuthorizationPairs = [];
      if (Array.isArray(authorizationsData)) {
        filterAuthorizationPairs = authorizationsData.map(entry => ({
          fundId: entry.fundId,
          investorId: entry.investorId
        }));
      }
      const filteredFundInvestors = fundInvestors.data.filter(entry =>
        filterAuthorizationPairs.some(
          pair =>
            pair.fundId === entry.fundId &&
            (pair.investorId === entry.investorId || pair.investorId === '*')
        )
      );

      filteredFundInvestors.forEach(item => {
        _funds[item.fundId] = {
          id: item.fundId,
          name: item.fundName
        };
        const _investor = {
          id: item.investorId,
          name: item.investorName
        };
        _investors[item.investorId] = _investor;
        if (!_fundInvestorsMap[item.fundId]) {
          _fundInvestorsMap[item.fundId] = {};
        }
        _fundInvestorsMap[item.fundId][item.investorId] = _investor;
      });

      for (let fundId in _fundInvestorsMap) {
        _fundInvestorsMap[fundId] = Object.values(_fundInvestorsMap[fundId]);
      }

      if (filteredFundInvestors.length === 0) {
        //Contact is FA and we need only Fund choices
        const fundChoicesForFaContact = {};
        fundInvestors.data.forEach(item => {
          if (!fundChoicesForFaContact[item.fundId]) {
            fundChoicesForFaContact[item.fundId] = {
              id: item.fundId,
              name: item.fundName
            };
          }
          setFundChoices(Object.values(fundChoicesForFaContact));
        });
      } else {
        setFundChoices(Object.values(_funds));
      }
      setInvestorChoices(Object.values(_investors));
      setFundInvestorsMap(_fundInvestorsMap);
    } else {
      setFundChoices(Object.values({}));
      setInvestorChoices(Object.values({}));
      setFundInvestorsMap({});
    }
  }, [
    fundInvestors.data,
    funds.data,
    investors.data,
    authorizationsData,
    fundInvestors
  ]);

  const hasWriteUserNotificationPermission = useMemo(() => {
    return permissions.includes('AUTH_WRITE_USER_NOTIFICATIONS');
  }, [permissions]);

  const handleSave = useCallback(
    async newData => {
      try {
        setNotificationContext(context => ({ ...context, saving: true }));
        const dataToUpdate = Object.values(newData).map(item =>
          omit(item, 'id')
        );
        await UserNotificationApi.update(dataToUpdate, userId);
        notify('ra.notification.custom_updated_message');
        setNotificationContext(context => ({ ...context, saving: false }));
        refresh();
      } catch (error) {
        setNotificationContext(context => ({ ...context, saving: false }));
        notifyServerError(error);
      }
    },
    [userId, notify, refresh, setNotificationContext, notifyServerError]
  );

  const listProps = pick(props, ['hasCreate', 'hasEdit', 'hasList', 'hasShow']);

  const [dialogProps, setDialogProps] = useState({
    fundsChoices: [],
    fundInvestors: {},
    allInvestors: [],
    id: '',
    userAddressesTypesAvailable: [],
    documentTypes: [],
    loading: false
  });
  useEffect(() => {
    const newDialogProps = {
      fundsChoices: fundChoices,
      fundInvestors: fundInvestorsMap,
      allInvestors: investorChoices,
      id: userId,
      userAddressesTypesAvailable: userAddressTypesAvailable,
      documentTypes: docTypeChoices,
      loading: fundInvestors.loading
    };

    setDialogProps(prevDialogProps => {
      // Only update if there's a change
      if (JSON.stringify(prevDialogProps) !== JSON.stringify(newDialogProps)) {
        return newDialogProps;
      }
      return prevDialogProps;
    });
  }, [
    fundChoices,
    fundInvestorsMap,
    investorChoices,
    userAddressTypesAvailable,
    userId,
    docTypeChoices,
    fundInvestors.loading
  ]);

  return (
    <RecordContextProvider
      value={notificationContext.data}
      key={notificationTabKey}
    >
      <List
        {...listProps}
        resource="usernotifications"
        filter={{ userId: userId }}
        pagination={null}
        title={<div />}
        basePath="/usernotifications"
        className={classes.listContainer}
        exporter={null}
        empty={
          <Empty
            dialogProps={dialogProps}
            hasWriteUserNotificationPermission={
              hasWriteUserNotificationPermission
            }
            disabled={fundInvestors.loading}
          />
        }
        actions={
          <ActionsToolbar
            dialogProps={dialogProps}
            hasWriteUserNotificationPermission={
              hasWriteUserNotificationPermission
            }
            disabled={fundInvestors.loading}
          />
        }
        bulkActionButtons={
          hasWriteUserNotificationPermission && (
            <BulkActionsToolbarButtons
              formProps={formProps}
              userId={props.id}
              context={notificationContext}
            />
          )
        }
      >
        <CustomProvider
          context={notificationContext}
          setContext={setNotificationContext}
          providerNotificationContext={providerNotificationContext}
        >
          <CustomForm
            save={handleSave}
            saving={notificationContext.saving}
            customRef={formProps}
            context={notificationContext}
            setContext={setNotificationContext}
            hasWriteUserNotificationPermission={
              hasWriteUserNotificationPermission
            }
            history={props.history}
          >
            <UserNotificationDatagrid
              investorChoices={investorChoices}
              fundChoices={fundChoices}
              documentTypes={docTypeChoices}
              userphonetypes={userphonetypes}
              userAddressTypesAvailable={userAddressTypesAvailable}
              userAddressTypes={userAddressTypesAll}
              fundInvestors={fundInvestorsMap}
              fundsLoading={funds.loading}
              investorsLoading={investors.loading}
              fundInvestorsLoading={fundInvestors.loading}
              userId={userId}
            />
          </CustomForm>
        </CustomProvider>
      </List>
    </RecordContextProvider>
  );
};

export default UserNotificationTab;
